import {DialogProvider, useDialogStore} from '@ariakit/react'
import {formatDisplayDateTime} from '@/dayjs'
import {useTranslation} from '@/translations/i18n'
import {Dialog, DialogBody, DialogDismiss, DialogHeader, DialogHeading} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {AlertCircleIcon, RefreshCw01Icon} from '@/components/ui/icon/Icon'
import {useGetBugReportIssueComments} from '../../hooks/useGetBugReportIssueComments'
import {BugReportIssue} from '../../types'
import {Markdown} from '../BugReportHistory/Markdown'
import {
    StyledCommentAuthor,
    StyledCommentContent,
    StyledCommentHeader,
    StyledCommentRow,
    StyledCommentsEmpty,
    StyledCommentsError,
    StyledCommentsFooter,
    StyledCommentsList,
    StyledCommentsState,
    StyledCommentTimestamp
} from './style'

interface BugReportCommentsDialogProps {
    issue: BugReportIssue | null
    isOpen: boolean
    onClose: () => void
}

export const BugReportCommentsDialog = ({issue, isOpen, onClose}: BugReportCommentsDialogProps) => {
    const dialogStore = useDialogStore({
        open: isOpen,
        setOpen: nextOpen => {
            if (!nextOpen) onClose()
        }
    })

    return (
        <DialogProvider store={dialogStore}>
            <Dialog width="640px">
                {isOpen && issue && <BugReportCommentsDialogShell issue={issue} onClose={onClose} />}
            </Dialog>
        </DialogProvider>
    )
}

const BugReportCommentsDialogShell = ({issue, onClose}: {issue: BugReportIssue; onClose: () => void}) => {
    const {t} = useTranslation()
    const {
        data: comments,
        isLoading,
        isError,
        error,
        refetch,
        isFetching
    } = useGetBugReportIssueComments(issue.id, {enabled: true})

    return (
        <>
            <DialogHeader>
                <DialogHeading>
                    {t('bug_reporter:comments_dialog_title')} · {issue.identifier}
                </DialogHeading>
                <DialogDismiss />
            </DialogHeader>

            <DialogBody gap={4}>
                {isLoading && (
                    <StyledCommentsState>
                        <Spinner size={24} />
                        {t('bug_reporter:comments_loading')}
                    </StyledCommentsState>
                )}

                {isError && (
                    <StyledCommentsError role="alert">
                        <AlertCircleIcon size={16} />
                        {error instanceof Error ? error.message : t('bug_reporter:comments_error')}
                    </StyledCommentsError>
                )}

                {!isLoading && !isError && comments?.length === 0 && (
                    <StyledCommentsEmpty>{t('bug_reporter:comments_empty')}</StyledCommentsEmpty>
                )}

                {!isLoading && !isError && !!comments?.length && (
                    <StyledCommentsList>
                        {comments.map(comment => {
                            const authorName = comment.user?.name || t('bug_reporter:comments_unknown_author')
                            return (
                                <StyledCommentRow key={comment.id}>
                                    <Avatar
                                        size="xs"
                                        name={authorName}
                                        imageUrl={comment.user?.avatarUrl ?? undefined}
                                    />
                                    <StyledCommentContent>
                                        <StyledCommentHeader>
                                            <StyledCommentAuthor>{authorName}</StyledCommentAuthor>
                                            <StyledCommentTimestamp>
                                                {formatDisplayDateTime(comment.createdAt)}
                                            </StyledCommentTimestamp>
                                        </StyledCommentHeader>
                                        <Markdown content={comment.body} />
                                    </StyledCommentContent>
                                </StyledCommentRow>
                            )
                        })}
                    </StyledCommentsList>
                )}
            </DialogBody>

            <StyledCommentsFooter>
                {isError && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
                        {isFetching ? <Spinner size={16} /> : <RefreshCw01Icon size={16} />}
                        {t('commons:retry')}
                    </Button>
                )}
                <Button type="button" variant="ghost" onClick={onClose}>
                    {t('commons:close')}
                </Button>
            </StyledCommentsFooter>
        </>
    )
}
